import { Injectable } from "@angular/core";
import { setLoadingAndError } from "@datorama/akita";
import { NiceToastService, showErrorToast } from "@recursyve/nice-ui-kit.v2";
import { EMPTY, Observable, catchError, map, tap, switchMap } from "rxjs";
import { ApiService } from "../../../api/api.service";
import { RegisterOwnersDto } from "../../../api/nerri/owners/dto/register-owners.dto";
import { RegisterStore } from "./register.store";

@Injectable()
export class RegisterService {
    constructor(
        private api: ApiService,
        private store: RegisterStore,
        private toastService: NiceToastService
    ) {}

    public reset(): void {
        this.store.reset();
    }

    public register(dto: Omit<RegisterOwnersDto, "uuid">): Observable<void> {
        const { owner } = this.store.getValue();
        if (!owner?.code?.longCode) {
            return EMPTY;
        }

        return this.api.owner.register({ ...dto, uuid: owner.code.longCode }).pipe(
            setLoadingAndError(this.store),
            showErrorToast(this.toastService, {
                messageKey: "features.register.toasts.error"
            }),
            tap(() => this.store.update({ hasRegistered: true })),
            switchMap(({ token }) => {
                if (!token) {
                    return EMPTY;
                }

                return this.api.session.loginWithToken(token).pipe(tap(() => this.store.update({ isLoggedIn: true })));
            }),
            catchError(() => EMPTY)
        );
    }

    public validateCode(code: string): Observable<void> {
        this.store.update({ hasValidationError: false });

        return this.api.owner.getCreationInfo({ code }).pipe(
            setLoadingAndError(this.store),
            map(owner => {
                this.store.update({ owner });
            }),
            catchError(() => {
                this.store.update({ hasValidationError: true });
                return EMPTY;
            })
        );
    }

    public validateUuid(uuid: string): Observable<void> {
        return this.api.owner.getCreationInfo({ uuid }).pipe(
            setLoadingAndError(this.store),
            showErrorToast(this.toastService, {
                messageKey: "features.register.invalid_link.toasts.error"
            }),
            map(owner => {
                this.store.update({ owner });
            }),
            catchError(() => EMPTY)
        );
    }
}
